import { useState, useMemo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { accountAccessShowLogin } from 'actions/UiActions'
import TextButton from 'LuxKit/Button/TextButton'
import PrizeModal from 'marketing/components/Competitions/PrizeModal'

const WEDGES_CONFIG = [
  { imageId: 'e4x9cz4vignfld7i7zd' },
  { imageId: '1tmx6zrnzujbik6uws1s' },
  { imageId: '6tnednzkeqocgea079u5' },
  { imageId: '1576wa7fdajsa69jl22k' },
  { imageId: 's07y20ll1t8vi0n9jlxe' },
  { imageId: 'c62yqo5w988nvfw9de4f' },
  { imageId: 'noww5i3vw5jukihrce87' },
  { imageId: '09uqg5p2nas70ielly5' },
]

const SPIN_TIME = 7

const PIN_IMAGE_ID = '154vxy9hvy46wyxb5le7'

const Container = styled.div`
  position: relative;
  aspect-ratio: 2 / 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${rem(20)} ${rem(20)} 0 ${rem(20)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(1140)};
    margin-left: auto;
    margin-right: auto;
  }
`

const WheelContainer = styled.div`
  aspect-ratio: 2 / 1;
  width: 100%;
  overflow: hidden;

`

const Wheel = styled.div<{ spinSpeed: number }>`
  position: relative;
  aspect-ratio: 1 / 1;
  width: 100%;
  border-radius: 50%;
  overflow: hidden;

  @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      85% {
        transform: rotate(${props => props.spinSpeed + 20}deg);
      }
      90% {
        transform: rotate(${props => props.spinSpeed - 5}deg);
      }
      95% {
        transform: rotate(${props => props.spinSpeed + 2}deg);
      }
      100% {
        transform: rotate(${props => props.spinSpeed}deg);
      }
    }

  &.spinned {
    animation: spin ${SPIN_TIME}s ease-in-out 0s 1 forwards;
  }

`

const WheelWedge = styled.div<{ value: number }>`
  position: absolute;
  width: 50%;
  height: 50%;
  left: 25%;
  transform-origin: center bottom;
  clip-path: polygon(50% 100%, 8.579% 0, 91.421% 0);
  transform: rotate(${props => (props.value * 45) + 22.5}deg);
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  cursor: pointer;
`

const PinMarker = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  left: 50%;
  margin-left: ${rem(-15)};
  width: ${rem(30)};

  ${mediaQueryUp.tablet} {
    width: ${rem(60)};
    top: -1%;
    margin-left: ${rem(-30)};
  }
`

const CTAButton = styled(TextButton)`
  position: absolute;
  top: 50%;
  margin: auto;
`

interface Props {
  prize: any
  setAnimationFinished: (status: boolean) => void
  isAnimationFinished: boolean
  memberId?: string
  fetchPrize: any
  competitionTitle: string
  breadcrumb: string
  promoLink?: string
  drawDate: string
  isModalOpen: boolean
  closeModal: () => void
}

function WheelOfPrize({
  prize, setAnimationFinished, memberId, isAnimationFinished, breadcrumb, fetchPrize, competitionTitle, promoLink, drawDate, closeModal,
  isModalOpen,
}: Props) {
  const intl = useIntl()
  const [wheelSpinning, setWheelSpinning] = useState(false)
  const dispatch = useAppDispatch()
  const spinSpeed = useMemo(() => 22.5 + 0.125 * (Math.floor(Math.random() * 10) + 1) * 10800, [])
  const spinTheWheel = useCallback(() => {
    if (memberId) {
      setWheelSpinning(true)
      fetchPrize()
      setTimeout(() => {
        setAnimationFinished(true)
      }, SPIN_TIME * 1000)
    }
  }, [fetchPrize, memberId, setAnimationFinished])

  const onSignin = useCallback(() => dispatch(accountAccessShowLogin()), [dispatch])

  const signUpLabel = intl.formatMessage({
    id: 'KGhzo5hmO8U01MDl0K0c',
    defaultMessage: 'Sign up or log in to spin',
    description: 'Button text prompting unauthenticated users to sign up or log in to spin the wheel of prize',
  })
  const clickToSpinLabel = intl.formatMessage({
    id: 'IHFu_1AJT0zNxnsC6Fy0',
    defaultMessage: 'Click to spin',
    description: 'Button text for authenticated users to spin the wheel of prize',
  })

  return (
    <Container>
      <WheelContainer>
        <Wheel className={clsx({ spinned: wheelSpinning })} spinSpeed={spinSpeed}>
          {WEDGES_CONFIG.map((config, index) => <WheelWedge value={index} key={index}>
            <ResponsiveImage id={config.imageId} />
          </WheelWedge>)}
        </Wheel>
        <PinMarker id={PIN_IMAGE_ID} />
      </WheelContainer>
      {!memberId && <CTAButton
        kind="primary"
        rel="noopener noreferrer"
        target="_blank"
        onClick={onSignin}
      >{signUpLabel}
      </CTAButton>}
      {(memberId && !wheelSpinning) && <CTAButton
        kind="primary"
        rel="noopener noreferrer"
        target="_blank"
        onClick={spinTheWheel}
      >{clickToSpinLabel}
      </CTAButton>}
      <PrizeModal
        isAnimationFinished={isAnimationFinished}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        prize={prize}
        drawDate={drawDate}
        promoLink={promoLink}
        breadcrumb={breadcrumb}
        competitionTitle={competitionTitle}
      />

    </Container>
  )
}

export default WheelOfPrize
